import React, { useMemo, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { calculateAssessmentScores } from '@/lib/scoring-engine';
import { computeEvidenceReadiness } from '@/lib/evidence-readiness';

/**
 * StackHealthTrend - visualises a client's ML2 / Evidence Readiness / Gating
 * Pass %  posture across every past assessment for that client.
 *
 * Data flow:
 *  - Fetches GET /api/assessments/trend?client_name=X (returns every past
 *    assessment for that client with raw `answers`)
 *  - Runs the SAME scoring engine (`calculateAssessmentScores`) and
 *    `computeEvidenceReadiness` client-side against each historical answer
 *    set, so trend numbers stay consistent with any live scoring updates.
 *
 * Rendering rules:
 *  - Hidden entirely when the client has fewer than 2 assessments (nothing
 *    to trend).
 *  - Shows loading skeleton while fetching.
 *  - Delta pill next to each metric = (latest - previous).
 */
const METRICS = [
  { key: 'ml2', label: 'ML2 Maturity', color: '#4f46e5' },       // indigo-600
  { key: 'evidence', label: 'Evidence Readiness', color: '#10b981' }, // emerald-500
  { key: 'gating', label: 'Gating Pass', color: '#f59e0b' },     // amber-500
];

const formatDate = (isoDate) => {
  try {
    return new Date(isoDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return isoDate?.slice(0, 10) || '';
  }
};

const StackHealthTrend = ({ clientName, currentAssessmentDate }) => {
  const { authFetch } = useAuth();
  const [rows, setRows] = useState(null); // null = loading, [] = none, [{...}] = data
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setShowSkeleton(false);
    // Only show the skeleton if the fetch takes longer than 250ms so brief
    // lookups (or negative cases with <2 assessments) don't flash a loader.
    const skeletonTimer = setTimeout(() => {
      if (!cancelled) setShowSkeleton(true);
    }, 250);
    const run = async () => {
      if (!clientName) { clearTimeout(skeletonTimer); return; }
      try {
        const res = await authFetch(
          `/api/assessments/trend?client_name=${encodeURIComponent(clientName)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          clearTimeout(skeletonTimer);
          setRows(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          clearTimeout(skeletonTimer);
          console.error('Stack Health Trend fetch failed:', err);
          setError(err.message || 'Failed to load trend data');
          setRows([]);
        }
      }
    };
    run();
    return () => { cancelled = true; clearTimeout(skeletonTimer); };
  }, [clientName, authFetch]);

  // Compute per-assessment metrics using the live scoring engine
  const chartData = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return rows.map(row => {
      const answers = row.answers || {};
      let ml2 = 0;
      let gating = 0;
      let evidence = 0;
      try {
        const scores = calculateAssessmentScores(answers);
        ml2 = Math.round(scores?.enhanced?.finalScore || 0);
        const gs = scores?.gatingStatus;
        if (gs && gs.total > 0) {
          gating = Math.round((gs.score2Count / gs.total) * 100);
        }
      } catch (err) {
        console.warn('Score compute failed for row', row.id, err);
      }
      try {
        const evid = computeEvidenceReadiness(answers);
        evidence = evid?.overall_percent ?? 0;
      } catch (err) {
        console.warn('Evidence compute failed for row', row.id, err);
      }
      return {
        id: row.id,
        dateISO: row.created_at,
        date: formatDate(row.created_at),
        assessor: row.user_name || row.user_email || '—',
        status: row.status,
        ml2,
        gating,
        evidence,
      };
    });
  }, [rows]);

  // Hide if fewer than 2 assessments — nothing to trend
  if (rows === null) {
    if (!showSkeleton) return null;
    return (
      <Card className="rounded-3xl border-0 shadow-sm" data-testid="stack-health-trend-loading">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-100 animate-pulse">
              <Activity className="h-5 w-5 text-indigo-500" />
            </div>
            <div>
              <CardTitle>Stack Health Trend</CardTitle>
              <CardDescription>Loading historical assessments…</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64 rounded-xl bg-slate-100 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (chartData.length < 2) return null;

  const latest = chartData[chartData.length - 1];
  const previous = chartData[chartData.length - 2];

  const deltaFor = (key) => {
    const d = (latest[key] || 0) - (previous[key] || 0);
    return d;
  };

  const renderDeltaPill = (delta) => {
    if (delta === 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
          <Minus className="h-3 w-3" />
          <span>0%</span>
        </span>
      );
    }
    const isUp = delta > 0;
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
          isUp ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
               : 'bg-red-50 text-red-700 border border-red-200'
        }`}
      >
        {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        <span>{isUp ? '+' : ''}{delta}%</span>
      </span>
    );
  };

  return (
    <Card className="rounded-3xl border-0 shadow-sm" data-testid="stack-health-trend">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-600">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle>Stack Health Trend</CardTitle>
              <CardDescription>
                {chartData.length} assessments for {clientName} · {formatDate(chartData[0].dateISO)} → {formatDate(latest.dateISO)}
              </CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700">
            vs previous ({formatDate(previous.dateISO)})
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Delta strip */}
        <div className="grid gap-3 sm:grid-cols-3" data-testid="stack-health-trend-deltas">
          {METRICS.map((m) => (
            <div
              key={m.key}
              className="rounded-2xl border border-slate-200 bg-white p-3"
              data-testid={`stack-health-trend-delta-${m.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {m.label}
                </span>
                {renderDeltaPill(deltaFor(m.key))}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold" style={{ color: m.color }}>
                  {latest[m.key]}%
                </span>
                <span className="text-xs text-slate-400">
                  was {previous[m.key]}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Line chart */}
        <div className="h-64 w-full" data-testid="stack-health-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} tickMargin={8} />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                formatter={(value, name) => [`${value}%`, name]}
                labelFormatter={(label, payload) => {
                  const p = payload?.[0]?.payload;
                  if (!p) return label;
                  return `${label} · ${p.assessor}`;
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                iconType="line"
              />
              {METRICS.map((m) => (
                <Line
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  name={m.label}
                  stroke={m.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Per-assessment table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="stack-health-trend-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="pb-2 font-medium">Date</th>
                <th className="pb-2 font-medium">Assessor</th>
                <th className="pb-2 font-medium text-right">ML2</th>
                <th className="pb-2 font-medium text-right">Evidence</th>
                <th className="pb-2 font-medium text-right">Gating Pass</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {chartData.map((row, i) => {
                const isLatest = i === chartData.length - 1;
                return (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-100 last:border-b-0 ${isLatest ? 'bg-slate-50' : ''}`}
                  >
                    <td className="py-2">
                      {row.date}
                      {isLatest && (
                        <span className="ml-2 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                          Latest
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-slate-500">{row.assessor}</td>
                    <td className="py-2 text-right font-medium" style={{ color: METRICS[0].color }}>{row.ml2}%</td>
                    <td className="py-2 text-right font-medium" style={{ color: METRICS[1].color }}>{row.evidence}%</td>
                    <td className="py-2 text-right font-medium" style={{ color: METRICS[2].color }}>{row.gating}%</td>
                    <td className="py-2 text-slate-500">{(row.status || '').replace(/_/g, ' ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {error && (
          <p className="text-xs text-red-600" data-testid="stack-health-trend-error">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default StackHealthTrend;
